import React, { useState, useEffect, createContext } from 'react'
import useAxios from 'axios-hooks'
import { Modal, Form } from 'antd'
import { getAuthHeader } from 'utils/auth'
import { freeSearch } from 'utils/search'
import { PageAlert, notificationToast } from 'components'
import ProjectListTop from 'views/UserManagement/Projects/components/ProjectListTop'
import ProjectList from 'views/UserManagement/Projects/components/ProjectList'
import ProjectForm from 'views/UserManagement/Projects/components/ProjectForm'
import FormFooter from 'views/ScanSettings/components/ScanFilterBar/FormFooter'

const formKeys = {
	NAME: 'name',
	PROJECT_USERS_IS_ADMIN: 'projectUsersIsAdmin',
	PROJECT_USERS_IN_PROJECT: 'projectUsersInProject'
}

const SEARCH_MAPPING = {
	name: 'name'
}

export const ProjectsContext = React.createContext()

function Projects({ form }) {
	const [projectsData, setProjectsData] = useState([])
	const [possibleUsers, setPossibleUsers] = useState([])
	const [currentEditProject, setCurrentEditProject] = useState(null)
	const [projectFormVisible, setProjectFormVisible] = useState(false)

	// project list
	const [
		{ data: listProjectsResponse, loading: listProjectsLoading, error: listProjectsError },
		getProjects
	] = useAxios({
		url: '/projects',
		method: 'GET',
		headers: getAuthHeader(),
		validateStatus: false
	})

	// possible users
	const [{ data: possibleUsersData, loading: possibleUsersDataLoading, error: possibleUsersDataError }] = useAxios({
		url: '/projects/users',
		method: 'GET',
		headers: getAuthHeader(),
		validateStatus: false
	})

	// possible users for specific project
	const [
		{ data: possibleProjectUsers, loading: possibleProjectUsersLoading, error: possibleProjectUsersError },
		getProjectUsers
	] = useAxios(
		{
			method: 'GET',
			headers: getAuthHeader(),
			validateStatus: false
		},
		{ manual: true }
	)

	// ADD Project
	const [{ data: newProjectData, loading: addingProject, error: addProjectError }, addNewProject] = useAxios(
		{
			url: '/projects',
			method: 'POST',
			headers: getAuthHeader()
		},
		{ manual: true }
	)
	// UPDATE Project
	let [{ data: updateProjectData, loading: updatingProject, error: updateProjectError }, updateProject] = useAxios(
		{
			url: '/projects',
			method: 'PUT',
			headers: getAuthHeader(),
			validateStatus: false
		},
		{ manual: true }
	)

	const openNotification = (message, errors) => {
		if (!errors) {
			notificationToast({
				message: 'Success',
				description: message,
				icontype: 'check'
			})
		} else {
			notificationToast({
				message: 'Error',
				description: `${message} ${errors.join()}`,
				icontype: 'close-circle'
			})
		}
	}

	const onNewProject = () => {
		setProjectFormVisible(true)
	}

	const onSearch = term => {
		if (term && term.length) setProjectsData(freeSearch(projectsData, term, SEARCH_MAPPING))
		else {
			setProjectsData(listProjectsResponse)
		}
	}

	// close project form
	const closeProjectForm = () => {
		if (currentEditProject) {
			setCurrentEditProject(null)
		}
		setProjectFormVisible(false)
	}

	// Edit Project
	const onEdit = project => {
		setCurrentEditProject(project)
		setProjectFormVisible(true)
	}

	const onProjectFormAfterClose = () => {
		form.resetFields()
		setPossibleUsers(possibleUsersData)
	}

	const extractProjectUsers = values => {
		const usersInProject = Object.entries(values)
			.filter(([k, v]) => k.includes(formKeys.PROJECT_USERS_IN_PROJECT) && !!Boolean(v))
			.map(([k, v]) => k.split('/')[1])

		return Object.entries(values)
			.filter(([k, v]) => k.includes(formKeys.PROJECT_USERS_IS_ADMIN) && usersInProject.includes(k.split('/')[1]))
			.map(([k, v]) => ({
				user_id: k.split('/')[1],
				is_admin: v
			}))
	}
	const onEditSave = () => {
		form.validateFields((errors, values) => {
			if (!errors) {
				const users = extractProjectUsers(values)
				let updatesPayload = {
					id: currentEditProject.id,
					name: values[formKeys.NAME],
					users
				}

				updateProject({
					data: updatesPayload
				})
			}
		})
	}

	// New Project
	const addProject = () => {
		form.validateFields((errors, values) => {
			if (!errors) {
				addNewProject({
					data: {
						name: values[formKeys.NAME],
						users: extractProjectUsers(values)
					}
				})
			}
		})
	}
	// for list of projects
	useEffect(() => {
		if (listProjectsResponse && listProjectsResponse.length) {
			setProjectsData(listProjectsResponse)
		}
	}, [listProjectsResponse])
	// for list of all possible users in new project
	useEffect(() => {
		if (possibleUsersData && possibleUsersData.length) {
			setPossibleUsers(possibleUsersData)
		}
	}, [possibleUsersData])
	// for list of possible users in existing project
	useEffect(() => {
		if (possibleProjectUsers && possibleProjectUsers.length) {
			setPossibleUsers(possibleProjectUsers)
		}
	}, [possibleProjectUsers])
	// for fetching the list of possible users by project
	useEffect(() => {
		if (currentEditProject) {
			getProjectUsers({
				url: `/projects/${currentEditProject.id}/users`
			})
		}
	}, [currentEditProject])
	// for creation of new project
	useEffect(() => {
		if (newProjectData) {
			closeProjectForm()
			openNotification('Project was created successfully.')
			getProjects()
		}
		if (addProjectError) {
			const errorMessage = addProjectError?.response?.data?.Errors
			openNotification('Cannot create project.', errorMessage)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [newProjectData, addProjectError, getProjects])
	// for project update request
	useEffect(() => {
		if (updateProjectData) {
			if(updateProjectData.Errors)
			{
				openNotification('Cannot update project.', updateProjectData.Errors)
			}else {
				closeProjectForm()
				openNotification('Project was edited successfully.')
				getProjects()
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [updateProjectData, updateProjectError, getProjects])

	return (
		<ProjectsContext.Provider value={{ form, onEdit, formKeys, possibleUsers }}>
			{listProjectsError && <PageAlert message={listProjectsError} />}
			<ProjectListTop onNewProject={onNewProject} onSearch={onSearch} />
			<ProjectList data={projectsData} loading={listProjectsLoading} />
			<Modal
				title={currentEditProject ? `Edit Project: ${currentEditProject.name}` : 'New Project'}
				width={600}
				visible={projectFormVisible}
				closable={false}
				maskClosable={false}
				destroyOnClose={true}
				afterClose={onProjectFormAfterClose}
				footer={
					<FormFooter
						onOK={currentEditProject ? onEditSave : addProject}
						onClose={closeProjectForm}
						okText="SUBMIT"
						confirmLoading={updatingProject || addingProject}
					/>
				}
			>
				<ProjectForm project={currentEditProject} form={form} formKeys={formKeys} />
			</Modal>
		</ProjectsContext.Provider>
	)
}

export default Form.create({})(Projects)